import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabaseClient';

interface PhotoUploadFieldProps {
  onPhotosChange: (urls: string[]) => void;
  maxPhotos?: number;
}

export default function PhotoUploadField({ onPhotosChange, maxPhotos = 10 }: PhotoUploadFieldProps) {
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    // Check total photos limit
    if (photos.length + files.length > maxPhotos) {
      setError(`You can only upload up to ${maxPhotos} photos`);
      return;
    }

    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Only JPG, PNG, and HEIC images are allowed');
      return;
    }

    // Validate file sizes (max 5MB each)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Each photo must be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const uploadedUrls: string[] = [];

      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
        const filePath = `job-photos/${fileName}`;

        const { data, error: uploadError } = await supabase.storage
          .from('job-images')
          .upload(filePath, file);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error('Failed to upload photo');
        }

        const { data: { publicUrl } } = supabase.storage
          .from('job-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      const newPhotos = [...photos, ...uploadedUrls];
      setPhotos(newPhotos);
      onPhotosChange(newPhotos);
    } catch (err) {
      console.error('Photo upload error:', err);
      setError('Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    onPhotosChange(newPhotos);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-gray-700">
          Add Photos (Optional)
        </label>
        <span className="text-xs text-gray-500">
          {photos.length}/{maxPhotos} photos
        </span>
      </div>

      <p className="text-sm text-gray-600">
        Photos help providers understand the job and quote faster.
      </p>

      {/* Upload Button */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/heic"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading || photos.length >= maxPhotos}
        />
        
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || photos.length >= maxPhotos}
          className={`w-full px-6 py-4 border-2 border-dashed rounded-xl transition-all flex items-center justify-center gap-3 ${
            uploading || photos.length >= maxPhotos
              ? 'border-gray-300 bg-gray-50 text-gray-400 cursor-not-allowed'
              : 'border-[#ff751f] bg-[#ff751f]/5 text-[#ff751f] hover:bg-[#ff751f]/10 cursor-pointer'
          }`}
        >
          {uploading ? (
            <>
              <i className="ri-loader-4-line text-xl animate-spin"></i>
              <span className="font-medium">Uploading...</span>
            </>
          ) : (
            <>
              <i className="ri-camera-line text-2xl"></i>
              <span className="font-medium">
                {photos.length === 0 ? 'Upload Photos' : 'Add More Photos'}
              </span>
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Photo Thumbnails */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {photos.map((photo, index) => (
            <div key={index} className="relative group">
              <img
                src={photo}
                alt={`Upload ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
              />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-red-600"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* File Requirements */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>• Supported formats: JPG, PNG, HEIC</p>
        <p>• Maximum file size: 5MB per photo</p>
        <p>• Maximum {maxPhotos} photos total</p>
      </div>
    </div>
  );
}
